import React, { createContext, ReactNode, useContext, useMemo, useReducer, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { useParams } from 'react-router-dom';
import {
  builderStep2Data,
  builderStep2DataEndOfStatement,
  builderStep3Data,
  IBuilderMediaData,
  IBuilderStep2Data,
} from '../components/Pages/MessageManager/Builder/Data';
import { ICriteriaData } from '../components/Pages/MessageManager/Builder/CriteriaList';
import { ApplicationTypeEnum } from '../components/Pages/MessageManager/Builder/Step1';
import { loadBuilderSnapshot, saveBuilderSnapshot } from '../components/Pages/MessageManager/Builder/storage';
import {
  getCampaignById,
  getNextCampaignId,
  upsertCampaignFromBuilder,
} from '../components/Pages/Dashboard/data/messageManager';

export type IModeType = 'image' | 'text' | undefined;
export const TabCount = ['One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten'];

export interface IBuilderStep1 {
  companyName: string;
  type: keyof typeof ApplicationTypeEnum | undefined;
}

export interface ISelectedModule {
  item: IBuilderStep2Data | undefined;
  imageTabIndex?: number;
  textTabIndex?: number;
}

export interface IBuilderStep2 {
  statement: {
    isEdit: boolean;
    data: IBuilderStep2Data[];
    statement: boolean;
    mode: IModeType;
    selectedAsDefaultId: string | undefined;
  };
  endOfStatement: {
    data: IBuilderStep2Data[];
    selected?: IBuilderStep2Data;
  };
}

export interface IBuilderStep3 {
  isEdit: boolean;
  data: ICriteriaData[];
}

export interface IBuilderStep4 {
  startDate: Date;
  endDate: Date;
}

export interface IBuilderState {
  step1: IBuilderStep1;
  step2: IBuilderStep2;
  step3: IBuilderStep3;
  step4: IBuilderStep4;
}

interface MessageManagerProviderProps {
  selectedImage: IBuilderMediaData | undefined;
  selectedModule: ISelectedModule | undefined;
  isTextOnly: boolean;
  activeTab: number;
  hideEndOfStatement: boolean;
  handleHideEndOfStatement: () => void;
  handleIsTextOnly: (payload: boolean) => void;
  handleSelectedModule: (payload?: ISelectedModule) => void;
  handleDispatch: (payload: Partial<IBuilderState>) => void;
  handleSelectedImage: (payload?: IBuilderMediaData) => void;
  handleActiveTab: (payload: number) => void;
  handleSave: () => IBuilderState;
  state: IBuilderState;
}

const defaultValue: MessageManagerProviderProps = {
  state: {
    step1: { companyName: '', type: undefined },
    step2: {
      statement: {
        isEdit: false,
        data: [],
        statement: true,
        selectedAsDefaultId: undefined,
        mode: undefined,
      },
      endOfStatement: {
        data: [],
      },
    },
    step3: { isEdit: false, data: [] },
    step4: { startDate: dayjs().toDate(), endDate: dayjs().toDate() },
  },
  isTextOnly: false,
  selectedImage: undefined,
  selectedModule: undefined,
  activeTab: 0,
  hideEndOfStatement: false,
  handleHideEndOfStatement: () => undefined,
  handleSelectedModule: () => undefined,
  handleSelectedImage: () => undefined,
  handleDispatch: () => undefined,
  handleIsTextOnly: () => undefined,
  handleActiveTab: () => undefined,
  handleSave: () => defaultValue.state,
};

function getEmptyBuilderState(): IBuilderState {
  return {
    step1: { companyName: '', type: undefined },
    step2: {
      statement: {
        isEdit: false,
        data: [],
        statement: true,
        selectedAsDefaultId: undefined,
        mode: undefined,
      },
      endOfStatement: {
        data: [],
      },
    },
    step3: { isEdit: false, data: [] },
    step4: { startDate: dayjs().toDate(), endDate: dayjs().toDate() },
  };
}

function getAppTypeFromCampaign(appType?: string): keyof typeof ApplicationTypeEnum {
  const normalized = appType?.toLowerCase() || '';

  if (normalized.includes('quarter')) return 'quarterly';
  if (normalized.includes('annual')) return 'annually';

  return 'monthly';
}

function getDefaultEditState(id?: string): IBuilderState {
  const campaign = getCampaignById(id);

  return {
    step1: {
      companyName: campaign?.campaignName || 'Test Campaign',
      type: getAppTypeFromCampaign(campaign?.appType),
    },
    step2: {
      statement: {
        isEdit: true,
        data: structuredClone(builderStep2Data),
        statement: true,
        selectedAsDefaultId: '3',
        mode: 'image',
      },
      endOfStatement: {
        data: structuredClone(builderStep2DataEndOfStatement),
      },
    },
    step3: { isEdit: true, data: structuredClone(builderStep3Data) },
    step4: {
      startDate: campaign?.scheduledDates.from ? new Date(campaign.scheduledDates.from) : dayjs().toDate(),
      endDate: campaign?.scheduledDates.to ? new Date(campaign.scheduledDates.to) : dayjs().add(1, 'week').toDate(),
    },
  };
}

function getDefaultBuilderState(id?: string): IBuilderState {
  return id ? getDefaultEditState(id) : getEmptyBuilderState();
}

function getSelectedModuleFromState(state: IBuilderState): ISelectedModule | undefined {
  const firstModule = state.step2.statement.data[0];

  if (!firstModule) return undefined;

  return { item: firstModule, imageTabIndex: 0, textTabIndex: undefined };
}

function mergePendingEdits(state: IBuilderState, selectedModule?: ISelectedModule): IBuilderState {
  const statementData = selectedModule?.item
    ? state.step2.statement.data.map(item => (item.id === selectedModule.item?.id ? selectedModule.item : item))
    : state.step2.statement.data;
  const endOfStatementSelected = state.step2.endOfStatement.selected;
  const endOfStatementData = endOfStatementSelected
    ? state.step2.endOfStatement.data.map(item =>
        item.id === endOfStatementSelected.id ? endOfStatementSelected : item,
      )
    : state.step2.endOfStatement.data;

  return {
    ...state,
    step2: {
      ...state.step2,
      statement: {
        ...state.step2.statement,
        data: statementData,
      },
      endOfStatement: {
        ...state.step2.endOfStatement,
        data: endOfStatementData,
        selected: endOfStatementSelected,
      },
    },
  };
}

export const MessageManagerContext = createContext<MessageManagerProviderProps>(defaultValue);

function MessageManagerProvider({ children }: { children: ReactNode }) {
  const { id } = useParams();
  const campaignIdRef = useRef(id);
  const [initialState] = useState(() => loadBuilderSnapshot(id) ?? getDefaultBuilderState(id));
  const [selectedImage, setSelectedImage] = useState<IBuilderMediaData>();
  const [selectedModule, setSelectedModule] = useState<ISelectedModule | undefined>(() =>
    getSelectedModuleFromState(initialState),
  );
  const [isTextOnly, setIsTextOnly] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [hideEndOfStatement, setHideEndOfStatement] = useState<boolean>(false);

  const [state, dispatch] = useReducer(
    (prevState: IBuilderState, action: Partial<IBuilderState>) => ({ ...prevState, ...action }),
    initialState,
  );

  const handleActiveTab = (index: number) => {
    setActiveTab(index);
  };

  const handleHideEndOfStatement = () => {
    setHideEndOfStatement(!hideEndOfStatement);
    handleActiveTab(0);
  };

  const handleIsTextOnly = (payload: boolean) => {
    setIsTextOnly(payload);
  };

  const handleSelectedModule = (payload?: ISelectedModule) => {
    setSelectedModule(payload);
  };

  const handleSelectedImage = (payload?: IBuilderMediaData) => {
    setSelectedImage(payload);
  };

  const handleDispatch = (payload: Partial<IBuilderState>) => {
    dispatch(payload);
  };

  const handleSave = () => {
    const nextState = mergePendingEdits(state, selectedModule);

    if (!campaignIdRef.current) {
      campaignIdRef.current = String(getNextCampaignId());
    }

    const campaignId = campaignIdRef.current;

    dispatch(nextState);
    saveBuilderSnapshot(campaignId, nextState);
    upsertCampaignFromBuilder(Number(campaignId), {
      campaignName: nextState.step1.companyName,
      type: nextState.step1.type,
      startDate: nextState.step4.startDate,
      endDate: nextState.step4.endDate,
    });

    return nextState;
  };

  const memoizedValue: MessageManagerProviderProps = useMemo(
    () => ({
      state,
      selectedImage,
      selectedModule,
      isTextOnly,
      activeTab,
      hideEndOfStatement,
      handleDispatch,
      handleSelectedImage,
      handleSelectedModule,
      handleIsTextOnly,
      handleActiveTab,
      handleHideEndOfStatement,
      handleSave,
    }),
    [state, selectedImage, selectedModule, isTextOnly, activeTab, hideEndOfStatement],
  );

  return <MessageManagerContext.Provider value={memoizedValue}>{children}</MessageManagerContext.Provider>;
}

export default MessageManagerProvider;

export const useMessageManagerProvider = (): MessageManagerProviderProps => useContext(MessageManagerContext);
